import { type MouseEvent, useCallback, useContext, useMemo, useState } from "react";

import {
    type DistributionRestriction,
    type ExclusiveLensStatus,
    type LensDestination,
    type LensRejectionErrors,
    type MyLensesLensStatus,
    SurfaceType,
    type Visibility,
} from "../../gql-generated/types";
import { useLensPreviewState } from "../../lens-preview/hooks/useLensPreviewState";
import { useLensDetailsVisibility } from "../../lens-visibility/hooks/useLensDetailsVisibility";
import { LensSubmissionErrorsModal } from "../../lenses/components/modal";
import { useLensSubmissionErrorsModalState } from "../../lenses/hooks/useLensSubmissionErrorsModalState";
import { UserInfoContext } from "../../shared/api/contexts";
import { useToggleState } from "../../shared/hooks/useToggleState";
import { type LensPreviewMediaItem } from "../../shared/state";
import { type LensStatusPanelProps } from "../components/lens-source/LensStatusPanel";
import { LensDestinationsTooltipContent } from "../components/shared/LensDestinationsTooltipContent";
import { PublishModal } from "../components/shared/PublishModal";
import {
    ERROR_STATUSES,
    type LensActionsVisibilityState,
    detailsPageAvailableStatusEntry,
    makeDestinationsText,
    makeDisplayStatus,
} from "../state";

import { PublishLens, ViewDetails } from "./messages.generated";
import { useLensChangeTracker } from "./useLensChangeTracker";
import { useSubmissionErrors } from "./useSubmissionErrors";

export const useLensStatusPanel = (
    status: MyLensesLensStatus,
    lensId: string,
    lensName: string,
    iconUrl: string,
    destinations: readonly LensDestination[],
    distributionRestrictionsList: readonly DistributionRestriction[],
    destinationsLoading: boolean,
    rejectionErrors: LensRejectionErrors | undefined,
    actionsConfig: LensActionsVisibilityState,
    onLensDestinationHeaderClick: (event: MouseEvent<HTMLElement>) => void,
    surfaceType: readonly SurfaceType[],
    exclusiveLensStatus: ExclusiveLensStatus,
    lensFolderId?: string,
): Omit<LensStatusPanelProps, "visibility"> => {
    const userInfo = useContext(UserInfoContext);
    const { publishToSnapchatConfig, setVisibility } = actionsConfig;
    const hasErrors = !!rejectionErrors && (rejectionErrors.hasSubmissionErrors || !!rejectionErrors.heading);
    const isSpectaclesOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
    const { submissionErrors, loading: errorsLoading } = useSubmissionErrors(lensId);

    const [errorModalProps, showErrorModal] = useLensSubmissionErrorsModalState();
    const [publishModalProps, showPublishModal] = useToggleState();

    const { previewMediaItems, updatePreviewMedia } = useLensChangeTracker(lensId);

    const setPreviewMediaItems = useCallback(
        (items: LensPreviewMediaItem[]) => {
            updatePreviewMedia(items);
        },
        [updatePreviewMedia],
    );

    const lensPreviewProps = useLensPreviewState({
        status: status,
        previewMediaItems: previewMediaItems ?? [],
        setPreviewMediaItems,
    });

    const lensDetailsVisibilityConfig = useLensDetailsVisibility(lensId, exclusiveLensStatus, status, lensFolderId);

    const { changeVisibility, changeVisibilityLoading, lensVisibilityConfig } = lensDetailsVisibilityConfig;
    const { disabled: lensVisibilityDisabled, defaultVisibility } = lensVisibilityConfig;

    const [selectedVisibility, setSelectedVisibility] = useState<Visibility | undefined>(defaultVisibility);

    const onPublishModalAction = useCallback(async () => {
        if (publishToSnapchatConfig.disabled || changeVisibilityLoading) {
            return;
        }
        if (selectedVisibility) {
            await changeVisibility(selectedVisibility, publishModalProps.onCancel);
        }
    }, [selectedVisibility, changeVisibility, changeVisibilityLoading, publishToSnapchatConfig, publishModalProps]);

    const pubishModal = useMemo(
        () => (
            <PublishModal
                {...publishModalProps}
                // Lens data props
                lensName={lensName}
                lensIconUrl={iconUrl}
                lensSurfaceType={surfaceType}
                lensId={lensId}
                lensForUpdate={undefined}
                isNewLensPublication={false}
                // Visibility props
                visibilityConfig={lensVisibilityConfig}
                currentVisibility={selectedVisibility}
                onVisibilityChange={setSelectedVisibility}
                // Action props
                actionButtonText={<PublishLens />}
                onAction={() => void onPublishModalAction()}
                actionIsPending={changeVisibilityLoading}
                actionIsDisabled={publishToSnapchatConfig.disabled}
                // Other props
                lensPreviewProps={lensPreviewProps}
                exclusiveLensStatus={exclusiveLensStatus}
            />
        ),
        [
            lensName,
            lensId,
            iconUrl,
            publishModalProps,
            lensPreviewProps,
            changeVisibilityLoading,
            publishToSnapchatConfig.disabled,
            lensVisibilityConfig,
            setSelectedVisibility,
            selectedVisibility,
            surfaceType,
            exclusiveLensStatus,
            onPublishModalAction,
        ],
    );

    const errorModal = useMemo(() => {
        return (
            <LensSubmissionErrorsModal
                errorsLoading={errorsLoading}
                modalProps={errorModalProps}
                submissionErrors={submissionErrors}
            />
        );
    }, [errorModalProps, submissionErrors, errorsLoading]);

    return useMemo(() => {
        const {
            name: title,
            icon,
            color,
            isColorInverted,
        } = publishToSnapchatConfig.show ? detailsPageAvailableStatusEntry : makeDisplayStatus(status, destinations);

        const tagLine = makeDestinationsText(destinations, userInfo?.data?.getUserProfile.userId ?? "", status, title);

        const tooltipContent = (
            <LensDestinationsTooltipContent
                distributionRestrictionsList={distributionRestrictionsList}
                onLensDestinationHeaderClick={onLensDestinationHeaderClick}
                loading={destinationsLoading || !!userInfo?.loading}
                destinations={destinations}
                userId={userInfo?.data?.getUserProfile.userId ?? ""}
            />
        );

        const showErrorDetails = ERROR_STATUSES.has(status) && hasErrors;

        const showCta = showErrorDetails || publishToSnapchatConfig.show;

        const ctaText = showErrorDetails ? <ViewDetails /> : <PublishLens />;
        const onAction = showCta ? (showErrorDetails ? showErrorModal : showPublishModal) : undefined;

        const showVisibility = !showCta && !isSpectaclesOnly;
        const isVisibilityDisabled = !setVisibility || lensVisibilityDisabled;

        return {
            title,
            tagLine,
            color,
            isColorInverted,
            loading: destinationsLoading || !!userInfo?.loading,
            icon,
            showCta,
            isCtaDisabled: !showErrorDetails && publishToSnapchatConfig.disabled,
            ctaText,
            onAction,
            showVisibility,
            isVisibilityDisabled,
            lensDetailsVisibilityConfig,
            tooltipContent,
            errorModal: showErrorDetails ? errorModal : null,
            publishModal: publishToSnapchatConfig.show ? pubishModal : null,
            hasDistributionRestrictions: distributionRestrictionsList.length > 0,
        };
    }, [
        publishToSnapchatConfig.show,
        publishToSnapchatConfig.disabled,
        status,
        destinations,
        distributionRestrictionsList,
        onLensDestinationHeaderClick,
        userInfo?.data?.getUserProfile.userId,
        userInfo?.loading,
        destinationsLoading,
        hasErrors,
        showErrorModal,
        showPublishModal,
        setVisibility,
        lensDetailsVisibilityConfig,
        errorModal,
        pubishModal,
        isSpectaclesOnly,
        lensVisibilityDisabled,
    ]);
};
